import React, { useMemo, useEffect } from "react";
import { useDropzone } from "react-dropzone";
import { useSearchParams } from "react-router-dom";
import InputField from "components/fields/InputField";
import TextField from "components/fields/TextField";
import { useNewAgent } from "../../contexts/NewAgentContext";
import { useTranslation } from "hooks/useTranslation";
import { CREATE_AGENT_CATEGORIES, FUNCTIONAL_AGENT_TYPE_OPTIONS, AgentTypeValue } from "constants/agents";
import PureDropDown from "components/pure-dropdown";

const AgentDetails = () => {
  const { formData, setFormData } = useNewAgent();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const agentCategory = searchParams.get("category") as CREATE_AGENT_CATEGORIES | null;
  const isFunctionalFlow = agentCategory === CREATE_AGENT_CATEGORIES.FUNCTIONAL;

  // Set agent_type in formData based on category:
  // - If category is NOT FUNCTIONAL (SPECIALIST or ORCHESTRATOR) → set agent_type = category
  // - If category IS FUNCTIONAL → agent_type will be set from the type dropdown below
  useEffect(() => {
    if (agentCategory && agentCategory !== CREATE_AGENT_CATEGORIES.FUNCTIONAL) {
      setFormData((prev) => ({
        ...prev,
        agent_type: agentCategory as unknown as AgentTypeValue, // Cast to AgentTypeValue since string values match
      }));
    }
  }, [agentCategory, setFormData]);


  const functionalAgentTypeOptions = useMemo(() => {
    return FUNCTIONAL_AGENT_TYPE_OPTIONS.map((option) => ({
      value: option.value,
      label: t(option.labelKey),
    }));
  }, [t]);

  const { getRootProps, getInputProps } = useDropzone({
    onDrop: (acceptedFiles) => {
      if (acceptedFiles.length > 0) {
        setFormData({ ...formData, icon: acceptedFiles[0] });
      }
    },
    accept: {
      "image/*": [".png", ".jpg", ".jpeg", ".gif"],
    },
    maxFiles: 1,
  });

  return (
    <div className="h-full w-full rounded-[20px]   px-3 pt-7 md:px-8">
      <form className="  mx-auto w-full rounded-lg  ">
        <div className="mb-6">
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <InputField
                id="agentName"
                label={t('agents.newAgent.agentName')}
                placeholder={t('agents.newAgent.agentNamePlaceholder')}
                type="text"
                value={formData.name}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setFormData({ ...formData, name: e.target.value })
                }
              />
            </div>
            <div className="flex flex-col items-center">
              <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-200">
                {t('agents.newAgent.icon')}
              </label>
              <div {...getRootProps()} className="cursor-pointer">
                <input {...getInputProps()} />
                {formData.icon ? (
                  <img
                    src={URL.createObjectURL(formData.icon)}
                    alt={t('agents.newAgent.agentIcon')}
                    className="h-12 w-12 rounded-full border-2 border-gray-200 object-cover dark:border-gray-700"
                  />
                ) : (
                  <div className="flex h-12 w-12 items-center justify-center rounded-full border-2 border-dashed border-gray-300 bg-gray-50 transition-colors hover:bg-gray-100 dark:border-gray-600 dark:bg-gray-800 dark:hover:bg-gray-700">
                    <svg
                      className="h-5 w-5 text-gray-400 dark:text-gray-300"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                      />
                    </svg>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        <div className="mb-6">
          <TextField
            id="description"
            label={t('agents.description')}
            placeholder={t('agents.newAgent.descriptionPlaceholder')}
            cols={30}
            rows={3}
            value={formData.description}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
              setFormData({ ...formData, description: e.target.value })
            }
          />
        </div>
        {isFunctionalFlow && (
          <div className="mb-6">
            <PureDropDown
              label={t('agents.newAgent.type')}
              options={functionalAgentTypeOptions}
              value={formData.agent_type || undefined}
              onChange={(value) => {
                const selectedType = value as string;
                setFormData({ 
                  ...formData, 
                  agent_type: selectedType as AgentTypeValue // Set agent_type to the selected type (e.g., "AUTONOMOUS")
                });
              }}
              placeholder={t('agents.newAgent.selectType')}
              showClearButton={false}
              className="w-full"
              buttonClassName="rounded-xl border border-gray-200 bg-white/0 px-3 py-3 text-sm text-gray-900 hover:border-gray-300 focus:outline-none focus:ring-2 focus:ring-brand-500 dark:border-white/10 dark:bg-white/0 dark:text-white dark:hover:border-white/20"
            />
          </div>
        )}
      </form>
    </div>
  );
};

export default AgentDetails;
